import {
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Tooltip,
} from 'platform/components';
import {Grid, GridItem, Show, Space, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {head} from 'ramda';
import {isArray, isNilOrEmpty} from 'ramda-adjunct';

import {useGetManufacturersQuery} from '@dms/api/core';
import {useGetSuppliersQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {FormToDataGridConnectorProps} from 'features/eag-grid';

import {getOptionsFromManufacturers} from '../../utils/getOptionsFromManufacturers';
import {getOptionsFromSuppliers} from '../../utils/getOptionsFromSuppliers';
import {getSearchTypeOptions} from '../../utils/getSearchTypeOptions';

type PriceListExternalFiltersForm = Partial<{
  catalogNumber: string;
  searchType: string;
  name: string;
  manufacturerId: string;
  supplierId: string;
}>;

type PriceListExternalFiltersFormProps = FormToDataGridConnectorProps & TestIdProps;

const MINIMUM_LENGTH = 6;
const SEARCH_TYPES_WITH_MINIMUM_LENGTH = ['LIKE', 'STARTS_WITH', 'ENDS_WITH'];
const DEFAULT_SEARCH_TYPE = 'EQUALS';

const isMinLengthSearchType = (searchType: string | undefined) =>
  SEARCH_TYPES_WITH_MINIMUM_LENGTH.includes(searchType ?? '');

export function PriceListExternalFilters(props: PriceListExternalFiltersFormProps) {
  const {
    data: manufacturers,
    isLoading: areManufacturersLoading,
    isError: hasManufacturersError,
  } = useGetManufacturersQuery();

  const {
    data: suppliers,
    isLoading: areSuppliersLoading,
    isError: hasSuppliersError,
  } = useGetSuppliersQuery();

  const isLoading = areManufacturersLoading || areSuppliersLoading;
  const hasError = hasManufacturersError || hasSuppliersError;

  const searchType = isArray(props.values.searchType)
    ? head(props.values.searchType)
    : props.values.searchType;

  const values = {
    ...props.values,
    searchType: searchType ?? DEFAULT_SEARCH_TYPE,
  };

  const handleOnSubmit: FormSubmitHandler<PriceListExternalFiltersForm> = async (
    data,
    setErrors
  ) => {
    const searchType = data.searchType;
    const rawCatalogNumber = data.catalogNumber;
    const rawName = data.name;
    const catalogNumber = rawCatalogNumber?.trim() ?? '';
    const name = rawName?.trim() ?? '';

    // Validation rules:
    // - LIKE/STARTS_WITH/ENDS_WITH search types require catalogNumber >= 6 chars;
    //   shorter input auto-switches to EQUALS.
    // - At least one of catalogNumber or name must be filled.
    // - If catalogNumber is present but too short for the active search type,
    //   show a minimum-length error; otherwise show a "both fields required" error.
    const shouldAutoSwitchToEquals =
      isMinLengthSearchType(searchType) &&
      catalogNumber.length > 0 &&
      catalogNumber.length < MINIMUM_LENGTH;

    const effectiveSearchType = shouldAutoSwitchToEquals ? DEFAULT_SEARCH_TYPE : searchType;

    const isCatalogNumberNilOrEmpty = isNilOrEmpty(catalogNumber);
    const shouldApplyMinimumLengthValidation = isMinLengthSearchType(effectiveSearchType);
    const isCatalogNumberTooShort =
      shouldApplyMinimumLengthValidation &&
      catalogNumber.length > 0 &&
      catalogNumber.length < MINIMUM_LENGTH;
    const isNameNilOrEmpty = isNilOrEmpty(name);
    const isNameTooShort = name.length > 0 && name.length < MINIMUM_LENGTH;

    const isValidationError =
      (isCatalogNumberNilOrEmpty || isCatalogNumberTooShort) &&
      (isNameNilOrEmpty || isNameTooShort);

    if (!isValidationError) {
      await props.onChange({...data, searchType: effectiveSearchType});
      return;
    }

    match({isCatalogNumberTooShort, isNameTooShort})
      .with({isCatalogNumberTooShort: true}, () => {
        setErrors([
          {
            name: 'catalogNumber',
            message: i18n.t('page.warehousePriceList.label.minimumLengthIsSixthCharacters'),
          },
        ]);
      })
      .with({isNameTooShort: true}, () => {
        setErrors([
          {
            name: 'name',
            message: i18n.t('page.warehousePriceList.label.minimumLengthIsSixthCharacters'),
          },
        ]);
      })
      .otherwise(() => {
        setErrors([
          {
            name: 'catalogNumber',
            message: i18n.t('page.warehousePriceList.label.warehouseValidationMessage'),
          },
          {
            name: 'name',
            message: i18n.t('page.warehousePriceList.label.warehouseValidationMessage'),
          },
        ]);
      });
  };

  return (
    <DataStatus isLoading={isLoading} isError={hasError}>
      <Form<PriceListExternalFiltersForm>
        defaultValues={{}}
        values={values}
        onSubmit={handleOnSubmit}
      >
        {(control, formApi) => {
          const rawCatalogNumber = formApi.watch('catalogNumber');
          const searchType = formApi.watch('searchType');
          const rawName = formApi.watch('name');
          const catalogNumber = rawCatalogNumber?.trim() ?? '';
          const name = rawName?.trim() ?? '';

          const shouldDisableMinLengthTypes = catalogNumber.length < MINIMUM_LENGTH;
          const filteringMethodOptions = getSearchTypeOptions(shouldDisableMinLengthTypes);

          const shouldAutoSwitchToEquals =
            shouldDisableMinLengthTypes && isMinLengthSearchType(searchType);

          if (shouldAutoSwitchToEquals) {
            formApi.setValue('searchType', DEFAULT_SEARCH_TYPE);
          }

          const isCatalogNumberNilOrEmpty = isNilOrEmpty(catalogNumber);
          const shouldApplyMinimumLengthValidation = isMinLengthSearchType(searchType);
          const isCatalogNumberTooShort =
            shouldApplyMinimumLengthValidation &&
            catalogNumber.length > 0 &&
            catalogNumber.length < MINIMUM_LENGTH;
          const isNameNilOrEmpty = isNilOrEmpty(name);
          const isNameTooShort = name.length > 0 && name.length < MINIMUM_LENGTH;

          const isValidationError =
            (isCatalogNumberNilOrEmpty || isCatalogNumberTooShort) &&
            (isNameNilOrEmpty || isNameTooShort);

          match({isValidationError, isCatalogNumberTooShort, isNameTooShort})
            .with({isValidationError: true, isCatalogNumberTooShort: true}, () => {
              formApi.setError('catalogNumber', {
                message: i18n.t('page.warehousePriceList.label.minimumLengthIsSixthCharacters'),
              });
              formApi.clearErrors('name');
            })
            .with({isValidationError: true, isNameTooShort: true}, () => {
              formApi.clearErrors('catalogNumber');
              formApi.setError('name', {
                message: i18n.t('page.warehousePriceList.label.minimumLengthIsSixthCharacters'),
              });
            })
            .with({isValidationError: true}, () => {
              formApi.setError('catalogNumber', {
                message: i18n.t('page.warehousePriceList.label.warehouseValidationMessage'),
              });
              formApi.setError('name', {
                message: i18n.t('page.warehousePriceList.label.warehouseValidationMessage'),
              });
            })
            .otherwise(() => {
              formApi.clearErrors();
            });

          return (
            <VStack spacing={4}>
              <Grid columns={3} align={isValidationError ? 'flex-start' : 'flex-end'}>
                <GridItem span={2}>
                  <FormField
                    control={control}
                    name="catalogNumber"
                    type="text"
                    label={i18n.t('entity.warehouse.labels.catalogueNumber')}
                    data-testid={suffixTestId('inputs.catalogueNumber', props)}
                  />
                </GridItem>
                <GridItem span={1}>
                  <Show when={isValidationError}>
                    <Space vertical={5} />
                  </Show>
                  <FormField
                    control={control}
                    name="searchType"
                    type="choice"
                    options={filteringMethodOptions}
                    isNotClearable
                    formatOptionLabel={(option, formatOptionLabelMeta) => {
                      if (formatOptionLabelMeta.context === 'menu' && option.isDisabled) {
                        return (
                          <div style={{pointerEvents: 'auto'}}>
                            <Tooltip
                              label={i18n.t(
                                'page.warehousePriceList.label.minimumLengthIsSixthCharacters'
                              )}
                            >
                              <span>{option.label}</span>
                            </Tooltip>
                          </div>
                        );
                      }
                      return option.label;
                    }}
                    data-testid={suffixTestId('inputs.filteringMethod', props)}
                  />
                  <Show when={isValidationError}>
                    <Space vertical={8} />
                  </Show>
                </GridItem>
              </Grid>
              <FormField
                control={control}
                name="name"
                type="text"
                label={i18n.t('entity.warehouse.labels.name')}
                data-testid={suffixTestId('inputs.name', props)}
              />
              <FormField
                control={control}
                name="manufacturerId"
                type="multiChoice"
                label={i18n.t('entity.warehouse.labels.manufacturer')}
                options={getOptionsFromManufacturers(manufacturers)}
                data-testid={suffixTestId('inputs.manufacturer', props)}
              />
              <FormField
                control={control}
                name="supplierId"
                type="multiChoice"
                label={i18n.t('entity.warehouse.labels.supplier')}
                options={getOptionsFromSuppliers(suppliers)}
                data-testid={suffixTestId('inputs.supplier', props)}
              />
              <Grid columns={4}>
                <GridItem span={2}>
                  <FormButton
                    control={control}
                    type="submit"
                    variant="secondary"
                    title={i18n.t('general.actions.apply')}
                    data-testid={suffixTestId('apply', props)}
                  />
                </GridItem>
              </Grid>
            </VStack>
          );
        }}
      </Form>
    </DataStatus>
  );
}
